import React, { useState } from 'react';
import { 
  ArrowLeft, Camera, Upload, Save, Store, MapPin, Phone, Mail, Globe, 
  Clock, Image, FileText, MessageSquare, Star, Check, X, Edit3, Trash2
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { MODULES } from '../data/database';

const BusinessProfilePage = ({ onBack, onNavigate }) => {
  const { user, selectedOffice } = useApp();
  
  // Initialize profile from current business context
  const biz = selectedOffice || {};
  const [profile, setProfile] = useState({
    name: biz.name || 'La tua attivita',
    category: biz.type || 'salon',
    subcategory: biz.category || '',
    description: biz.description || '',
    shortBio: biz.shortDescription || biz.description?.substring(0, 80) || '',
    highlightMessage: 'Sconto 20% sui trattamenti!',
    highlightMessageEnabled: true,
    logo: biz.image || '',
    coverImage: biz.images?.[0] || biz.image || '',
    gallery: biz.images || [],
    address: typeof biz.address === 'object' ? {
      street: biz.address?.street || '',
      city: biz.address?.city || '',
      province: biz.address?.province || '',
      zip: biz.address?.zip || '',
      country: 'Italia',
    } : {
      street: biz.address || '',
      city: '',
      province: '',
      zip: '',
      country: 'Italia',
    },
    coordinates: { lat: biz.lat || 40.8358, lng: biz.lng || 14.5089 },
    contacts: {
      phone: biz.phone || '',
      email: biz.email || user?.email || '',
      website: biz.website || '',
      whatsapp: biz.phone || '',
    },
    social: {
      instagram: '',
      facebook: '',
      tiktok: '',
    },
    vatNumber: '',
    isElitPartner: biz.isElit || user?.isElitPartner || false,
  });

  const [activeSection, setActiveSection] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);

  const updateProfile = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const updateNestedProfile = (parent, field, value) => {
    setProfile(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // In real app, would save to backend
    setHasChanges(false);
    alert('Modifiche salvate con successo!');
  };

  // Ottieni configurazione dal modulo abilitato dell'office
  const officeModules = selectedOffice?.enabledModules || ['appointments'];
  const primaryModule = MODULES[officeModules[0]] || MODULES.appointments;

  const sections = [
    { id: 'general', name: 'Generale', icon: Store },
    { id: 'media', name: 'Media', icon: Image },
    { id: 'location', name: 'Posizione', icon: MapPin },
    { id: 'contacts', name: 'Contatti', icon: Phone },
    { id: 'highlight', name: 'In evidenza', icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-4">
              <button 
                onClick={onBack} 
                className="p-2 -ml-2 hover:bg-gray-100 rounded-full"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Vetrina</h1>
                <p className="text-sm text-gray-500">Personalizza il profilo della tua attività</p>
              </div>
            </div>
            {hasChanges && (
              <button
                onClick={handleSave}
                className="flex items-center gap-2 bg-emerald-500 text-white px-4 py-2 rounded-xl font-medium hover:bg-emerald-600 transition-colors"
              >
                <Save className="w-4 h-4" />
                Salva
              </button>
            )}
          </div>

          {/* Section Tabs */}
          <div className="flex gap-1 px-4 pb-3 overflow-x-auto">
            {sections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                    isActive 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{section.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto p-4">
        {/* General Section */}
        {activeSection === 'general' && (
          <div className="space-y-6">
            {/* Business Type */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Tipo attività</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(MODULES).map(([id, module]) => (
                  <button
                    key={id}
                    onClick={() => updateProfile('category', id)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      profile.category === id
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-medium text-gray-900 text-sm">{module.name}</p>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-1">{module.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Basic Info */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Informazioni base</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome attività *</label>
                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) => updateProfile('name', e.target.value)}
                    className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sottocategoria</label>
                  <input
                    type="text"
                    value={profile.subcategory}
                    onChange={(e) => updateProfile('subcategory', e.target.value)}
                    placeholder="Es. Parrucchiere, Pizzeria, Hotel 4 stelle..."
                    className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bio breve</label>
                  <input
                    type="text"
                    value={profile.shortBio}
                    onChange={(e) => updateProfile('shortBio', e.target.value)}
                    placeholder="Una frase che descrive la tua attività"
                    maxLength={100}
                    className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">{profile.shortBio?.length || 0}/100 caratteri</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione completa</label>
                  <textarea
                    value={profile.description}
                    onChange={(e) => updateProfile('description', e.target.value)}
                    rows={4}
                    placeholder="Descrivi la tua attività in dettaglio..."
                    className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Partita IVA</label>
                  <input
                    type="text"
                    value={profile.vatNumber}
                    onChange={(e) => updateProfile('vatNumber', e.target.value)}
                    placeholder="IT00000000000"
                    className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Media Section */}
        {activeSection === 'media' && (
          <div className="space-y-6">
            {/* Logo */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Logo</h3>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <img 
                    src={profile.logo} 
                    alt="Logo" 
                    className="w-24 h-24 rounded-2xl object-cover border-2 border-gray-200"
                  />
                  <button className="absolute -bottom-2 -right-2 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-emerald-600">
                    <Camera className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-2">Carica il logo della tua attività</p>
                  <p className="text-xs text-gray-400">Formato: JPG, PNG. Max 2MB. Dimensione consigliata: 200x200px</p>
                </div>
              </div>
            </div>

            {/* Cover Image */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Immagine di copertina</h3>
              <div className="relative rounded-xl overflow-hidden">
                <img 
                  src={profile.coverImage} 
                  alt="Copertina" 
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                  <button className="flex items-center gap-2 bg-white text-gray-900 px-4 py-2 rounded-xl font-medium">
                    <Upload className="w-4 h-4" />
                    Cambia immagine
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">Dimensione consigliata: 1200x400px</p>
            </div>

            {/* Gallery */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Galleria</h3>
                <button className="flex items-center gap-1 text-emerald-600 text-sm font-medium">
                  <Upload className="w-4 h-4" />
                  Aggiungi
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {profile.gallery.map((img, index) => (
                  <div key={index} className="relative group">
                    <img 
                      src={img} 
                      alt={`Gallery ${index + 1}`} 
                      className="w-full h-24 object-cover rounded-xl"
                    />
                    <button className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <button className="w-full h-24 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-400 hover:border-emerald-500 hover:text-emerald-500 transition-colors">
                  <Upload className="w-6 h-6 mb-1" />
                  <span className="text-xs">Aggiungi</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Location Section */}
        {activeSection === 'location' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Indirizzo</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Via/Piazza *</label>
                  <input
                    type="text"
                    value={profile.address.street}
                    onChange={(e) => updateNestedProfile('address', 'street', e.target.value)}
                    className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Città *</label>
                    <input
                      type="text"
                      value={profile.address.city}
                      onChange={(e) => updateNestedProfile('address', 'city', e.target.value)}
                      className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Provincia</label>
                    <input
                      type="text"
                      value={profile.address.province}
                      onChange={(e) => updateNestedProfile('address', 'province', e.target.value)}
                      className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
                    <input
                      type="text"
                      value={profile.address.zip}
                      onChange={(e) => updateNestedProfile('address', 'zip', e.target.value)}
                      className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Paese</label>
                    <input
                      type="text"
                      value={profile.address.country}
                      onChange={(e) => updateNestedProfile('address', 'country', e.target.value)}
                      className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Map Preview */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Posizione sulla mappa</h3>
              <div className="bg-gray-200 rounded-xl h-48 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <MapPin className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">Mappa interattiva</p>
                  <p className="text-xs">Trascina il pin per posizionare</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contacts Section */}
        {activeSection === 'contacts' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Contatti</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      value={profile.contacts.phone}
                      onChange={(e) => updateNestedProfile('contacts', 'phone', e.target.value)}
                      className="w-full bg-gray-100 rounded-xl pl-11 pr-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      value={profile.contacts.whatsapp}
                      onChange={(e) => updateNestedProfile('contacts', 'whatsapp', e.target.value)}
                      className="w-full bg-gray-100 rounded-xl pl-11 pr-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={profile.contacts.email}
                      onChange={(e) => updateNestedProfile('contacts', 'email', e.target.value)}
                      className="w-full bg-gray-100 rounded-xl pl-11 pr-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sito web</label>
                  <div className="relative">
                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="url"
                      value={profile.contacts.website}
                      onChange={(e) => updateNestedProfile('contacts', 'website', e.target.value)}
                      className="w-full bg-gray-100 rounded-xl pl-11 pr-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Social */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Social</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                  <input
                    type="text"
                    value={profile.social.instagram}
                    onChange={(e) => updateNestedProfile('social', 'instagram', e.target.value)}
                    placeholder="@username"
                    className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
                  <input
                    type="text"
                    value={profile.social.facebook}
                    onChange={(e) => updateNestedProfile('social', 'facebook', e.target.value)}
                    placeholder="pagina o username"
                    className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">TikTok</label>
                  <input
                    type="text"
                    value={profile.social.tiktok}
                    onChange={(e) => updateNestedProfile('social', 'tiktok', e.target.value)}
                    placeholder="@username"
                    className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Highlight Message Section */}
        {activeSection === 'highlight' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Messaggio in evidenza</h3>
                <button
                  onClick={() => updateProfile('highlightMessageEnabled', !profile.highlightMessageEnabled)}
                  className={`relative w-12 h-7 rounded-full transition-colors ${profile.highlightMessageEnabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
                >
                  <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${profile.highlightMessageEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Mostra un messaggio in evidenza sulla tua pagina. Ideale per promozioni, novità o comunicazioni importanti.
              </p>
              <textarea
                value={profile.highlightMessage}
                onChange={(e) => updateProfile('highlightMessage', e.target.value)}
                rows={3}
                placeholder="Es. Sconto 20% su tutti i trattamenti colore!"
                maxLength={200}
                disabled={!profile.highlightMessageEnabled}
                className={`w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500 resize-none ${!profile.highlightMessageEnabled ? 'opacity-50' : ''}`}
              />
              <p className="text-xs text-gray-400 mt-1">{profile.highlightMessage?.length || 0}/200 caratteri</p>
              
              {/* Preview */}
              {profile.highlightMessageEnabled && profile.highlightMessage && (
                <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
                  <p className="text-sm font-medium text-gray-700">Anteprima:</p>
                  <p className="text-amber-800 mt-1">{profile.highlightMessage}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessProfilePage;
